import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import Button from "../ui/Button";
import { useT } from "../../contexts/TranslationContext";
import {
  buildTaskMappingOptions,
  emptyExpectedCostForm,
} from "./expectedCostUtils";

export default function ExpectedMaterialCostFormModal({
  isOpen,
  projectId,
  taskBundle,
  editingAccrual = null,
  defaultTaskOverride = "",
  onClose,
  onSaved,
}) {
  const { t } = useT();
  const [form, setForm] = useState(emptyExpectedCostForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const taskOptions = useMemo(
    () => buildTaskMappingOptions(taskBundle),
    [taskBundle]
  );

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    if (editingAccrual) {
      setForm({
        taskOverride: editingAccrual.taskOverride || "",
        amount: String(editingAccrual.amount ?? ""),
        description: editingAccrual.description || "",
        supplierName: editingAccrual.supplierName || "",
        notes: editingAccrual.notes || "",
      });
    } else {
      setForm({
        ...emptyExpectedCostForm,
        taskOverride: defaultTaskOverride || taskOptions[0]?.value || "",
      });
    }
  }, [isOpen, editingAccrual, defaultTaskOverride, taskOptions]);

  if (!isOpen) return null;

  async function handleSave(e) {
    e.preventDefault();
    if (!projectId) return;
    const numAmount = Number(form.amount);
    if (
      !form.taskOverride ||
      !form.description?.trim() ||
      !Number.isFinite(numAmount) ||
      numAmount < 0
    ) {
      setError(t("projectDetails.expectedCostsFormInvalid"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        taskOverride: form.taskOverride,
        amount: numAmount,
        description: form.description.trim(),
        supplierName: form.supplierName?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
      };
      if (editingAccrual?._id || editingAccrual?.accrualId) {
        const accrualId = editingAccrual._id || editingAccrual.accrualId;
        await axios.patch(`/projects/${projectId}/material-accruals/${accrualId}`, payload);
      } else {
        await axios.post(`/projects/${projectId}/material-accruals`, payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || t("projectDetails.expectedCostsSaveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-bold mb-4">
          {editingAccrual
            ? t("projectDetails.expectedCostsEditTitle")
            : t("projectDetails.expectedCostsAddTitle")}
        </h3>
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        <form onSubmit={handleSave} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("projectDetails.taskSubtask")}
            </label>
            <select
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.taskOverride}
              onChange={(e) => setForm((f) => ({ ...f, taskOverride: e.target.value }))}
              required
            >
              {taskOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("projectDetails.expectedCostsDescription")}
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("projectDetails.expectedCostsSupplier")}
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.supplierName}
              onChange={(e) => setForm((f) => ({ ...f, supplierName: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("projectDetails.expectedCostsAmount")} ({t("projectDetails.expectedCostsAmountHint")})
            </label>
            <input
              type="number"
              min="0"
              step="1"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("projectDetails.expectedCostsNotes")}
            </label>
            <textarea
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              {t("projectDetails.cancel")}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? t("projectDetails.saving") : t("projectDetails.save")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
